import * as React from "react";
import dayjs, { Dayjs } from "dayjs";
import isBetweenPlugin from "dayjs/plugin/isBetween";
import { styled } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";

dayjs.extend(isBetweenPlugin);

interface CustomPickerDayProps extends PickersDayProps<Dayjs> {
  isSelected: boolean;
  isHovered: boolean;
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "isHovered",
})<CustomPickerDayProps>(({ theme, isSelected, isHovered, day }) => ({
  borderRadius: 0,
  // Selected week: lock styles, block all hover/focus overrides
  ...(isSelected && {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: "#fff !important",
    pointerEvents: "auto",
    "&:hover, &:focus, &:active, &.Mui-focusVisible": {
      backgroundColor: `${theme.palette.primary.main} !important`,
      color: "#fff !important",
    },
    "&.MuiPickersDay-root:hover": {
      backgroundColor: `${theme.palette.primary.main} !important`,
    },
  }),
  // Non-selected week hover
  ...(!isSelected &&
    isHovered && {
      backgroundColor: "#E0EDFF !important",
      color: "#000 !important",
      "&:hover, &:focus": {
        backgroundColor: "#E0EDFF !important",
        color: "#000 !important",
      },
    }),
  ...(day.day() === 0 && {
    borderTopLeftRadius: "100%",
    borderBottomLeftRadius: "100%",
  }),
  ...(day.day() === 6 && {
    borderTopRightRadius: "100%",
    borderBottomRightRadius: "100%",
  }),
})) as React.ComponentType<CustomPickerDayProps>;

const isInSameWeek = (dayA: Dayjs, dayB: Dayjs | null | undefined) => {
  if (dayB == null) {
    return false;
  }

  return dayA.isSame(dayB, "week");
};

function Day(
  props: PickersDayProps<Dayjs> & {
    selectedDay?: Dayjs | null;
    hoveredDay?: Dayjs | null;
  }
) {
  const { day, selectedDay, hoveredDay, ...other } = props;

  return (
    <CustomPickersDay
      {...other}
      day={day}
      sx={{ mx: 0 }}
      disableMargin
      selected={false}
      isSelected={isInSameWeek(day, selectedDay)}
      isHovered={isInSameWeek(day, hoveredDay)}
    />
  );
}

export default function WeekPicker({
  value,
  onChange,
}: {
  value: any;
  onChange: any;
}) {
  const [hoveredDay, setHoveredDay] = React.useState<Dayjs | null>(null);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateCalendar
        views={["year", "month", "day"]}
        value={value}
        onChange={(newValue) => onChange(newValue)}
        sx={{ height: "auto" }}
        slots={{ day: Day }}
        slotProps={{
          day: (ownerState) => ({
            selectedDay: value,
            hoveredDay,
            onPointerEnter: () => setHoveredDay(ownerState.day),
            onPointerLeave: () => setHoveredDay(null),
          }),
        }}
      />
    </LocalizationProvider>
  );
}
